import { Commit } from "vuex";

export type ErrorHandlerOptions = {
  /** Show a toast via notification/error. Default: false. */
  showNotification?: boolean;
  /** Only log to console (analytics / background work). Default: false. */
  silent?: boolean;
  /** Toast to show via notification/add when the mutation succeeded. */
  successMessage?: string;
};

/**
 * Normalize the many error shapes that end up in `vuefront/error`
 * (Apollo error objects, arrays of GraphQL errors, plain strings)
 * into a single user-displayable string.
 */
export const formatGraphQLError = (error: unknown): string => {
  if (!error) return "";
  if (typeof error === "string") return error;
  if (Array.isArray(error)) {
    const first = error[0];
    if (first && typeof first.message === "string") return first.message;
    return String(first ?? "Something went wrong");
  }
  const e = error as {
    message?: string;
    graphQLErrors?: { message?: string }[] & { message?: string };
  };
  if (e.graphQLErrors) {
    if (typeof e.graphQLErrors.message === "string") {
      return e.graphQLErrors.message;
    }
    if (Array.isArray(e.graphQLErrors) && e.graphQLErrors[0]?.message) {
      return e.graphQLErrors[0].message as string;
    }
  }
  if (typeof e.message === "string") return e.message;
  return "Something went wrong";
};

type ActionContextSlice = {
  commit: Commit;
  rootGetters: any;
};

/**
 * Standard post-mutation handling for actions that go through
 * `apollo/mutate` (which sets/resets `vuefront/error`).
 * Returns true when the mutation succeeded.
 */
export const handleMutationResult = (
  { commit, rootGetters }: ActionContextSlice,
  options: ErrorHandlerOptions = {}
): boolean => {
  const error = rootGetters["vuefront/error"];
  if (!error) {
    if (options.successMessage) {
      commit("notification/add", options.successMessage, { root: true });
    }
    return true;
  }
  if (options.silent) {
    // eslint-disable-next-line no-console -- silent mode: diagnostic warning instead of a user notification
    console.warn("[store] mutation failed", error);
    return false;
  }
  if (options.showNotification) {
    commit("notification/error", formatGraphQLError(error), { root: true });
  }
  return false;
};

/**
 * Standard catch-block handling for actions that call `$vfapollo`
 * directly (no `apollo/mutate`, so `vuefront/error` isn't managed
 * automatically). Records the error and optionally notifies the user.
 */
export const reportActionError = (
  { commit }: { commit: Commit },
  error: unknown,
  options: ErrorHandlerOptions = {}
): void => {
  commit("vuefront/setResponseError", error, { root: true });
  if (options.silent) {
    // eslint-disable-next-line no-console -- silent mode: diagnostic warning instead of a user notification
    console.warn("[store] action failed", error);
    return;
  }
  if (options.showNotification) {
    commit("notification/error", formatGraphQLError(error), { root: true });
  }
};
